import { css } from 'styled-components';

import { LAYOUT_HEADER_Z_INDEX } from 'approot/shared/layout/layout.constants';

/**
 * App-owned CSS for Astryx surfaces — the things `inqTheme` cannot express.
 *
 * Everything here exists because `defineTheme` has two hard limits:
 *
 * 1. **`tokens` is a closed set.** Its type is `Partial<Record<TokenName, …>>`,
 *    so a bespoke `--color-*` name is a compile error. Custom values have to be
 *    declared as plain CSS.
 * 2. **`components` takes a flat map of CSS properties per state**, with no
 *    pseudo-selector support — so `:focus-within`, and any rule that has to
 *    reach a child element, cannot live in the theme.
 *
 * One rule is here by choice rather than by limit: the AppShell header's
 * stacking order. `components` would take it, but z-index is layout plumbing
 * against the legacy app's ladder, not part of Inquisitive's visual language —
 * keeping `inqTheme` to the latter is what makes it readable as a theme.
 *
 * Kept out of `global.styles.tsx` so the Astryx-specific workarounds sit in one
 * place: each is load-bearing for a reason that isn't obvious from the rule
 * itself, and they should be revisited together whenever `@astryxdesign/core`
 * is upgraded — a future version may make some of them unnecessary.
 *
 * **Deliberately unlayered.** `global.styles.tsx` declares
 * `@layer inq-reboot, reset, astryx-base, astryx-theme`, and unlayered rules
 * beat every layer. That is what lets these override Astryx's own component
 * styles; putting them in a layer would make them lose.
 */
export const AstryxOverridesCss = css`
  /*
    Custom properties for Create AI, scoped to the Astryx subtree rather than
    :root.

    Astryx emits its own tokens inside \`@scope\` rules keyed on
    \`[data-astryx-theme]\`, so its tokens live and die with the themed subtree.
    Declaring these on the same element keeps them to the same lifetime and the
    same scope: custom properties inherit, so every Astryx descendant can read
    them, and nothing leaks to the rest of the app.

    They are named outside the \`--color-*\` namespace Astryx owns, so they can
    never collide with a real token — and \`webclient/CLAUDE.md\` forbids
    redefining \`--color-*\` anyway.

    Note these are static values: unlike a real theme token they take no
    [light, dark] pair, so they will not follow a mode switch. That is fine while
    \`inqTheme\` is light-only — revisit if a dark theme is ever added.
  */
  [data-astryx-theme] {
    /* Selected option row in the Create AI flow. */

    /* Flow progress bar. --color-success (green-8) is the dark green used for
       status text and reads far too heavy as a progress fill. */
    --inq-progress-fill: #73d13d; /* green-5 */
    --inq-progress-track: #f0f0f0;

    /* Quiet outline for the composer container. */
    --inq-border-subtle: #e8e8e8; /* grey-4 */

    /* Subject tints — the step-2 rung of each SubjectColours ramp in
       colours.style.tsx, keyed by the ramp name Subject.colour returns from
       startup data. Decorative panel fills behind a subject's illustration, with
       the subject name rendered beside the panel rather than on it, so nothing
       here carries meaning by colour alone. */
    --inq-subject-green: #d9f7be;
    --inq-subject-orange: #ffe7ba;
    --inq-subject-purple: #efdbff;
    --inq-subject-volcano: #ffd8bf;
    --inq-subject-gold: #fff1b8;
    --inq-subject-azure: #d6e4ff;
    --inq-subject-cyan: #b5f5ec;
    --inq-subject-magenta: #ffd6e7;
    --inq-subject-grey: #fafafa;
  }

  /*
    Purple focus ring on the Create AI composer.

    A theme override can't express it: the treatment is a :focus-within on the
    container, and component overrides carry no pseudo-selectors.
  */
  .astryx-chat-composer:focus-within {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-accent-muted);
  }

  /*
    One text weight across every create-type toggle, selected or not.

    Astryx shifts a pressed ToggleButton's label to semibold via a StyleX class
    on an inner span, so neither a theme override nor a prop can reach it — the
    child's own class wins. Both states are pinned rather than just the pressed
    one: overriding only the pressed state leaves the *unselected* buttons
    looking heavier than the selected one.
  */
  .astryx-toggle-button,
  .astryx-toggle-button span {
    font-weight: var(--font-weight-normal);
  }

  /*
    \`xs\` — a 16px size step below Astryx's smallest, opted into with
    \`className="xs"\`. Each definition is scoped to its component's own class,
    so the two never meet.

    Any 16px control is below WCAG 2.2 AA SC 2.5.8's 24×24 target minimum, so
    \`xs\` is only for a target that has knowingly accepted that exception — not
    a default to reach for.

    The size itself is declared here rather than in \`inqTheme\`'s \`tokens\` for
    limit 1 above: Astryx's \`TokenName\` union carries only sm/md/lg, so a
    bespoke step is a compile error there. Scoped to the Astryx subtree like the
    custom properties at the top of this file, and read from three places — the
    two rules below and \`button\` → \`variant:xs\` in inq-theme.ts, which is a
    component override rather than a token and so takes any CSS value.

    It keeps Astryx's \`--size-element-*\` name rather than an \`--inq-\` one,
    unlike the colours above: the theme already redeclares the sm/md/lg steps, so
    a future Astryx \`xs\` would be overridden here exactly as those are, not
    collide with something meaning anything else.
  */
  [data-astryx-theme] {
    --size-element-xs: 16px;
  }

  /*
    Button glyph. The 16px surface itself is a theme override
    (\`button\` → \`variant:xs\` in inq-theme.ts); only the glyph has to be here,
    because Icon renders its size as a StyleX literal on a child element and a
    theme override cannot reach a child.

    Matches the surface rather than insetting from it: a lucide glyph carries its
    own padding, so the element and the mark you see are not the same size. The
    \`X\` path runs 6→18 inside a 24×24 viewBox — half the box — so a 16px icon
    draws an 8px cross with 4px of clear space each side. Sizing the element to
    the 8px the design measures would render a 4px mark.
  */
  .astryx-button.xs .astryx-icon {
    width: var(--size-element-xs);
    height: var(--size-element-xs);
  }

  /*
    Checkbox — a 16px control box, against 24px for \`md\` and 20px for \`sm\`.

    None of this can be a theme override. CheckboxInput's sizes are StyleX
    literals with no custom property to remap, and every element carrying one is
    a child of the root — the only node a className reaches.

    Three elements have to move together or the control tears apart: the wrapper
    that reserves the row's space, the transparent native input that is the
    actual click target, and the visible box. The wrapper is addressed
    structurally because Astryx gives it no class; it is the first child of the
    row container, which is the first child of the root.
  */
  .astryx-checkbox-input.xs > div:first-child > div:first-child,
  .astryx-checkbox-input.xs input[type='checkbox'],
  .astryx-checkbox-input.xs .astryx-checkbox {
    width: var(--size-element-xs);
    height: var(--size-element-xs);
  }

  /* Tick, scaled to the smaller box — Astryx's is 14px, which fills a 16px box
     edge to edge. 10px holds roughly the ratio the untouched sizes use.

     Raw px because there is nothing to reference: Astryx ships no icon-size
     token at all (its \`--color-icon-*\` set is colour only), and Icon's own
     sizes are StyleX literals. */
  .astryx-checkbox-input.xs .astryx-checkbox svg {
    width: 10px;
    height: 10px;
  }

  /*
    AppShell header — stacked where the legacy fixed header sat.

    AppShell positions this wrapper itself (sticky, top 0) because we run it at
    height="auto", but pairs that with z-index 1, which loses to every legacy
    element that still assumes a header at LAYOUT_HEADER_Z_INDEX: the sticky
    content-page action bars (5), scroll-to-top (4) and the navigation overlay
    (101). Restating the legacy value keeps that whole ladder intact instead of
    re-deriving it for the shell.

    Astryx ships no z-index token, so there is nothing to remap in inqTheme —
    and stacking order is layout plumbing rather than a visual token, so it
    belongs here with the other structural workarounds.
  */
  .astryx-app-shell-header {
    z-index: ${LAYOUT_HEADER_Z_INDEX};
  }
`;
